import { derived, get, writable } from 'svelte/store';

import { DomainService } from '@client/services/domain';

import { branding as domainBranding } from './domain-settings';
import { popular } from './fonts';

const defaultValues = {
  backgroundcolor: '#ffffff',
  primary: '#0087ff',
  secondary: '#cfe2ff',
  tertiary: '#ff7a59',
  textcolor: '#212529',
  companyname: undefined,
  companyurl: undefined,
  fontfamily: undefined,
  headerfontfamily: undefined,
  fontweight: '300',
  headerfontweight: '700',
  icon: undefined,
  logo: undefined
};

export const colorBackground = writable(defaultValues.backgroundcolor);
export const colorPrimary = writable(defaultValues.primary);
export const colorSecondary = writable(defaultValues.secondary);
export const colorTertiary = writable(defaultValues.tertiary);
export const colorText = writable(defaultValues.textcolor);

export const companyName = writable(defaultValues.companyname);
export const companyUrl = writable(defaultValues.companyurl);

export const fontBase = writable(defaultValues.fontfamily);
export const fontHeadings = writable(defaultValues.headerfontfamily);
export const fontWeightBase = writable(defaultValues.fontweight);
export const fontWeightHeadings = writable(defaultValues.headerfontweight);

export const iconUrl = writable(defaultValues.icon);
export const logoUrl = writable(defaultValues.logo);

/**
 * Store map by key name
 * @type {Record<string, import('svelte/store').Writable<string | undefined>>}
 */
const brandStores = {
  companyname: companyName,
  companyurl: companyUrl,
  backgroundcolor: colorBackground,
  primary: colorPrimary,
  secondary: colorSecondary,
  tertiary: colorTertiary,
  textcolor: colorText,
  fontfamily: fontBase,
  headerfontfamily: fontHeadings,
  fontweight: fontWeightBase,
  headerfontweight: fontWeightHeadings,
  icon: iconUrl,
  logo: logoUrl
};

domainBranding.subscribe((currentBranding = {}) => {
  for (const serverKey in brandStores) {
    const store = brandStores[serverKey];
    const newValue = currentBranding[serverKey];
    const defaultValue = defaultValues[serverKey];

    // Use the value from the server if defined, otherwise fallback to default value.
    const valueToSet = newValue ?? defaultValue;

    // Only call `set` if the store's value is actually changing.
    if (get(store) !== valueToSet) {
      store.set(valueToSet);
    }
  }
});

/**
 * Whether the domain has completed the brand wizard.
 *
 * Reads the server-saved branding rather than the stores above, which always hold
 * non-empty defaults and so would be truthy for a domain that never ran the wizard.
 * The fields checked here are exactly the ones the wizard blocks Next on, so this is
 * true precisely when a full run has been saved.
 *
 * @type {import('svelte/store').Readable<boolean>}
 */
export const hasBranding = derived(domainBranding, ($branding) =>
  Boolean(
    $branding?.companyname &&
      $branding?.companyurl &&
      $branding?.fontfamily &&
      $branding?.headerfontfamily &&
      $branding?.logo &&
      $branding?.icon
  )
);

/**
 * Derived store of CSS custom properties for branding
 * @type {import('svelte/store').Readable<Record<string, string>>}
 */
export const brandCssVariables = derived(
  [
    colorBackground,
    colorPrimary,
    colorSecondary,
    colorTertiary,
    colorText,
    fontBase,
    fontHeadings,
    fontWeightBase,
    fontWeightHeadings,
    iconUrl,
    logoUrl
  ],
  ([
    $colorBackground,
    $colorPrimary,
    $colorSecondary,
    $colorTertiary,
    $colorText,
    $fontBase,
    $fontHeadings,
    $fontWeightBase,
    $fontWeightHeadings,
    $iconUrl,
    $logoUrl
  ]) => {
    const cssVariables = {
      '--brand-background-color': $colorBackground,
      '--brand-primary-color': $colorPrimary,
      '--brand-secondary-color': $colorSecondary,
      '--brand-tertiary-color': $colorTertiary,
      '--brand-text-color': $colorText,
      '--brand-font-family': $fontBase,
      '--brand-headings-font-family': $fontHeadings,
      '--brand-font-weight': $fontWeightBase,
      '--brand-headings-font-weight': $fontWeightHeadings,
      '--brand-icon-background-image': $iconUrl ? `url("${$iconUrl}")` : 'none',
      '--brand-logo-background-image': $logoUrl ? `url("${$logoUrl}")` : 'none'
    };

    return Object.fromEntries(Object.entries(cssVariables).filter(([_, value]) => value !== undefined));
  }
);

/**
 * Derived store for a Google Fonts URL based on selected fonts
 * @type {import('svelte/store').Readable<string | undefined>}
 */
export const fontsUrl = derived([fontBase, fontHeadings], ([$fontBase, $fontHeadings]) => {
  const fonts = [$fontBase, $fontHeadings].filter((font) => !popular.includes(font)).filter(Boolean);

  if (fonts.length === 0) {
    return undefined;
  }

  const uniqueFontNames = Array.from(new Set(fonts));
  const encodedFontNames = uniqueFontNames.map((name) => encodeURIComponent(name).replace(/%20/g, '+'));

  return `https://fonts.googleapis.com/css?family=${encodedFontNames.join('|')}&display=swap`;
});

/**
 * Push current branding values to the server
 * @returns {Promise<boolean>}
 */
export const updateBranding = async () => {
  /** @type {Record<string, string | undefined>} */
  const branding = {};

  for (const key in brandStores) {
    branding[key] = get(brandStores[key]);
  }

  try {
    const response = await DomainService.updateSettings({ branding });
    if (response.status !== 500) {
      return true;
    } else {
      console.error('Error updating branding', response?.status, response?.message);
      return false;
    }
  } catch (error) {
    console.error('Error updating branding', error);
    return false;
  }
};
